import { Action, getModule, Module, Mutation, VuexModule } from 'vuex-module-decorators';
import { store } from '../..';
import { GEnum } from '@/constants/enum.defination';
import { customCompetencyService } from '@/service/api/custom-competency.service';
import { Competency, CustomCompetency, CreateCustomCompetency, CompetencySelection, UpdateCustomCompetency, AddUpdateCustomCompetencyBoxplot } from '@/models/competency/competency';

export interface ICustomCompetencyState {
  customCompetencies: CustomCompetency[]; // 作成済みのカスタムコンピテンシー
  createCustomCompetency: CreateCustomCompetency; // 新規作成時
  editCustomCompetency: CreateCustomCompetency; // 編集・更新時
  selectionCognitive: CompetencySelection; // コンピテンシータイプ「認知」のチェックボックスグループ
  selectionSelf: CompetencySelection; // コンピテンシータイプ「自己」のチェックボックスグループ
  selectionGift: CompetencySelection; // コンピテンシータイプ「他者」のチェックボックスグループ
  selectionCommunity: CompetencySelection; // コンピテンシータイプ「コミュニティ」のチェックボックスグループ
  selectedCustomCompetencyBoxplot: number[];
}
@Module({ dynamic: true, store, name: 'customCompetencyModule', namespaced: true })
class CustomCompetencyModule extends VuexModule implements ICustomCompetencyState {
  customCompetencies: CustomCompetency[] = [];
  createCustomCompetency: CreateCustomCompetency = new CreateCustomCompetency();
  editCustomCompetency: UpdateCustomCompetency = new UpdateCustomCompetency();
  selectionCognitive: CompetencySelection = new CompetencySelection(GEnum.CompetencyType.Cognitive);
  selectionSelf: CompetencySelection = new CompetencySelection(GEnum.CompetencyType.Self);
  selectionGift: CompetencySelection = new CompetencySelection(GEnum.CompetencyType.Gift);
  selectionCommunity: CompetencySelection = new CompetencySelection(GEnum.CompetencyType.Community);
  selectedCustomCompetencyBoxplot: number[] = [];

  customCompetencyBoxplotForGraph: CustomCompetency[] = [];


  get selectedCompetencyIDs(): number[] {
    return [this.selectionCognitive.checkAllGroup, this.selectionSelf.checkAllGroup, this.selectionGift.checkAllGroup, this.selectionCommunity.checkAllGroup].flat();
  }

  @Mutation
  clean() {
    this.createCustomCompetency = new CreateCustomCompetency();
    this.selectionCognitive = new CompetencySelection(GEnum.CompetencyType.Cognitive);
    this.selectionSelf = new CompetencySelection(GEnum.CompetencyType.Self);
    this.selectionGift = new CompetencySelection(GEnum.CompetencyType.Gift);
    this.selectionCommunity = new CompetencySelection(GEnum.CompetencyType.Community);
  }

  @Mutation
  cleanSelectedCompetencies() {
    this.selectionCognitive = new CompetencySelection(GEnum.CompetencyType.Cognitive);
    this.selectionSelf = new CompetencySelection(GEnum.CompetencyType.Self);
    this.selectionGift = new CompetencySelection(GEnum.CompetencyType.Gift);
    this.selectionCommunity = new CompetencySelection(GEnum.CompetencyType.Community);
  }

  @Mutation
  setName(value: string) {
    this.createCustomCompetency.name = value;
  }

  @Mutation
  setUpdateName(value: string) {
    this.editCustomCompetency.name = value;
  }

  @Mutation
  setUpdateID(value: number) {
    this.editCustomCompetency.id = value;
  }

  @Mutation
  setCustomCompetencies(value: CustomCompetency[]) {
    this.customCompetencies = value;
  }

  @Mutation
  setCustomCompetencyBoxplotForGraph(value: CustomCompetency[]) {
    this.customCompetencyBoxplotForGraph = value;
  }

  @Mutation
  setSelectionCognitive(value: number) {
    this.selectionCognitive.checkAllGroup.push(value);
  }

  @Mutation
  setSelectionSelf(value: number) {
    this.selectionSelf.checkAllGroup.push(value);
  }

  @Mutation
  setSelectionGift(value: number) {
    this.selectionGift.checkAllGroup.push(value);
  }

  @Mutation
  setSelectionCommunity(value: number) {
    this.selectionCommunity.checkAllGroup.push(value);
  }

  /**
   * 作成済みカスタムコンピテンシーの取得
   */
  @Action({ rawError: true })
  async fetchCustomCompetency() {
    const result = await customCompetencyService.fetch();
    this.setCustomCompetencies(result);
  }

  /**
   * カスタムコンピテンシーとして作成済みで、グラフ表示設定しているカスタムコンピテンシーの取得
   */
  @Action({ rawError: true })
  async fetchCustomCompetencyBoxplotForGraph() {
    const result = await customCompetencyService.fetchCustomCompetencyBoxplotForGraph();
    this.setCustomCompetencyBoxplotForGraph(result);
  }

  /**
   * 新規作成 : カスタムコンピテンシー
   */
  @Action({ rawError: true })
  async addCustomCompetency() {
    this.createCustomCompetency.competencyIDs = this.selectedCompetencyIDs;
    const result = await customCompetencyService.createCustomCompetency(this.createCustomCompetency);
    return result;
  }

  /**
   * 新規作成 : カスタムコンピテンシー（受検画面から）
   */
  @Action({ rawError: true })
  async addCustomCompetencyAtEvalSetting() {
    const result = await customCompetencyService.createCustomCompetency(this.createCustomCompetency);
    return result;
  }

  /**
   * 編集・更新 : カスタムコンピテンシー
   */
  @Action({ rawError: true })
  async updateCustomCompetency() {
    this.editCustomCompetency.name = this.createCustomCompetency.name;
    this.editCustomCompetency.competencyIDs = this.selectedCompetencyIDs;
    const result = await customCompetencyService.updateCustomCompetency(this.editCustomCompetency);
    return result;
  }

  /**
   * 削除 : カスタムコンピテンシー
   */
  @Action({ rawError: true })
  async deleteCustomCompetency(id: number) {
    const result = await customCompetencyService.deleteCustomCompetency(id);
    return result;
  }

  /**
   * 取得 : カスタム箱ひげ図設定
   */
  @Action({ rawError: true })
  async fetchCustomCompetencyBoxplot() {
    const result = await customCompetencyService.fetchCustomCompetencyBoxplot();
    return result;
  }

  /**
   * 新規作成・更新 : カスタム箱ひげ図設定
   */
  @Action({ rawError: true })
  async addUpdateCustomCompetencyBoxplot(value: number[]) {
    let requestParam: AddUpdateCustomCompetencyBoxplot = {
      competencyIDs: value,
    };
    const result = await customCompetencyService.addUpdateCustomCompetencyBoxplot(requestParam);
    return result;
  }

}

export const customCompetencyModule = getModule(CustomCompetencyModule);
